import { useState } from "react";
import { Lock, Users, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const OPTIONS = [
  { key: "everyone", label: "Everyone on the project", icon: Globe },
  { key: "only_me", label: "Only me", icon: Lock },
  { key: "chosen", label: "Chosen people", icon: Users },
];

export function VisibilityBadge({ d }) {
  if (!d.visible_to?.length) return null;
  const onlyOne = d.visible_to.length === 1;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
      title={`Visible to: ${(d.visible_to_names || []).join(", ")}`}
      data-testid={`discussion-visibility-${d.id}`}
    >
      <Lock className="h-3 w-3" />
      {onlyOne ? "Private" : `${d.visible_to.length} people`}
    </span>
  );
}

export function AccessPicker({ mode, setMode, picked, setPicked, users }) {
  const { user } = useAuth();
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div className="space-y-2">
      {OPTIONS.map(({ key, label, icon: Icon }) => (
        <button
          type="button"
          key={key}
          onClick={() => setMode(key)}
          className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm text-left transition-colors ${mode === key ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
          data-testid={`access-option-${key}`}
        >
          <Icon className="h-4 w-4 text-slate-500" /> {label}
        </button>
      ))}
      {mode === "chosen" && (
        <div className="space-y-1 border border-slate-200 rounded-lg p-2 max-h-60 overflow-y-auto" data-testid="access-people-list">
          {users.filter((u) => u.id !== user?.id).map((u) => (
            <label key={u.id} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
              <Checkbox checked={picked.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`access-person-${u.id}`} />
              <span className="text-slate-900">{u.name}</span>
              <span className="text-xs text-slate-400 truncate">{u.email}</span>
            </label>
          ))}
          <p className="text-xs text-slate-400 px-2 pt-1">You always keep access. People added get a notification and email.</p>
        </div>
      )}
    </div>
  );
}

export function DiscussionAccessButton({ d, users, reload }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("everyone");
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  const onOpen = (v) => {
    setOpen(v);
    if (v) {
      const vt = d.visible_to || [];
      setMode(!vt.length ? "everyone" : vt.length === 1 && vt[0] === user?.id ? "only_me" : "chosen");
      setPicked(vt.length ? vt : d.created_by ? [d.created_by] : []);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/discussions/${d.id}/access`, { visibility: mode, user_ids: picked });
      toast.success("Access updated");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-300 hover:text-indigo-600 transition-colors p-1" aria-label="Who can see" data-testid={`discussion-access-btn-${d.id}`}>
          <Users className="h-4 w-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto" data-testid={`discussion-access-dialog-${d.id}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">Who can see this discussion?</DialogTitle>
        </DialogHeader>
        <AccessPicker mode={mode} setMode={setMode} picked={picked} setPicked={setPicked} users={users} />
        <Button onClick={save} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-700 text-white" data-testid="access-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
